import { Stack, Typography, useTheme } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import {
  ChecklistDualIcon,
  RecurringTicketsSidebarIcon,
  TagsDualIcon,
  TicketDualIcon,
} from "components";
import { usePermissionStore } from "controller";
import React from "react";
import { PERMISSIONS } from "shared";
import { TicketCreationType } from "../CreateTicketDrawer";
import { SidebarDetailsComponentWrapper } from "./SidebarDetailsComponentWrapper";

const BaseMenuList = [
  {
    label: "Ticket properties",
    icon: TicketDualIcon,
    key: "properties",
  },
  {
    label: "Tags",
    icon: TagsDualIcon,
    key: "tags",
  },
  {
    label: "Checklist",
    icon: ChecklistDualIcon,
    key: "checklist",
  },
];

const RecurringMenuItem = {
  label: "Recurring ticket",
  icon: RecurringTicketsSidebarIcon,
  key: "recurring",
};

export const CreateTicketSidebarComponent = ({
  type,
  lockMakeRecurringToggle = false,
  createdFrom = "ticket_drawer",
}: {
  type: TicketCreationType;
  /** When true, the recurring section hides its toggle (Control Center entry). */
  lockMakeRecurringToggle?: boolean;
  /** Entry point — forwarded so downstream sections can branch on it. */
  createdFrom?: "recurring_tickets" | "ticket_drawer";
}) => {
  const theme = useTheme();
  const [selectedKey, setSelectedKey] = React.useState<Record<
    string,
    any
  > | null>();
  const hasRecurringTicketRw = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.SD.SD_RECURRING_TICKET_RW]),
  );

  // Recurring option:
  //  - Not applicable for template create/edit.
  //  - Not shown when the toggle is locked (Control Center flow already renders
  //    the recurring section inline in the main form).
  //  - Gated behind SD_RECURRING_TICKET_RW so roles without the permission
  //    never see the entry point.
  const MenuList = React.useMemo(() => {
    if (type === "templateCreate" || type === "templateEdit")
      return BaseMenuList;
    if (lockMakeRecurringToggle) return BaseMenuList;
    if (!hasRecurringTicketRw) return BaseMenuList;
    return [...BaseMenuList, RecurringMenuItem];
  }, [type, lockMakeRecurringToggle, hasRecurringTicketRw]);

  const handleSelection = (value: Record<string, any>) => {
    setSelectedKey((prev) => (value === prev ? null : value));
  };
  return (
    <Collapse
      in={selectedKey ? true : false}
      collapsedSize={80}
      orientation="horizontal"
      sx={{
        height: "100%",
        backgroundColor: "#FFF",
        borderRadius: "8px",
        "&.MuiCollapse-entered": {
          borderRadius: "8px",
        },
      }}
    >
      <Stack direction={"row"} sx={{ height: "100%" }}>
        <Stack
          sx={{
            height: "100%",
            borderRadius: "8px 0px 0px 8px",
            p: "24px 16px",
            gap: "16px",
            width: "80px",
            boxShadow:
              "4px 0px 8px 0px rgba(0, 0, 0, 0.04), 2px 0px 4px 0px rgba(0, 0, 0, 0.01)",
          }}
        >
          {MenuList.map((item) => (
            <Stack key={item.key} gap="8px">
              <Stack
                onClick={() => handleSelection(item)}
                sx={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.neutral["015"]}`,
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor:
                    selectedKey?.key === item.key
                      ? theme.palette.secondary["purple-tint-3"]
                      : "none",
                  ":hover": {
                    cursor: "pointer",
                    backgroundColor: theme.palette.neutral["B-010"],
                  },
                }}
              >
                <item.icon htmlColor={theme.palette.purple.main} />
              </Stack>
              <Typography variant="body-xsmall" textAlign={"center"}>
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack
          sx={{
            width: "360px",
            height: "100%",
            borderRadius: "0px 8px 8px 0px",
            overflowY: "clip",
          }}
        >
          <SidebarDetailsComponentWrapper
            title={selectedKey?.label}
            onClose={() => setSelectedKey(null)}
            type={selectedKey?.key}
            ticketCreationType={type}
            lockMakeRecurringToggle={lockMakeRecurringToggle}
            createdFrom={createdFrom}
          />
        </Stack>
      </Stack>
    </Collapse>
  );
};
